import { Badge, Button, useDisclosure } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { EqualizationEditDateModal } from "features/closings/EqualizationEditDateModal";
import { ClosingTabProps } from "features/closings/tabs/ClosingTabProps";
import { useClosingRetrieveSuspense } from "gen/endpoints/closing/closing";
import {
  useEqualizationCreate,
  useEqualizationRetrieveSuspense,
} from "gen/endpoints/equalization/equalization";
import { useFundFamilyConfigRetrieveSuspense } from "gen/endpoints/fund-family-config/fund-family-config";
import {
  getFundFamilyEventRetrieveQueryKey,
  useFundFamilySubEventListSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { EqualizationConfigDateEnum } from "gen/model/equalizationConfigDateEnum";
import { FundFamilySubEventData } from "gen/model/fundFamilySubEventData";
import { FundFamilySubEventTypeEnum } from "gen/model/fundFamilySubEventTypeEnum";
import { find } from "lodash-es";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import EmptyState from "shared/components/empty/EmptyState";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBadges } from "shared/components/table/box/TableBoxBadges";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { StylesForCellFn } from "shared/components/table/data/DataTableTypes";
import { useToast } from "shared/components/toast/useToast";
import { useCeleryWorkflowPoll } from "shared/hooks/useCeleryWorkflowPoll";
import { Edit2Icon } from "shared/icons/Edit2Icon";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";

export const ClosingTabEqualizations: React.FC = () => {
  const { showToast, showErrorToast } = useToast();
  const { t } = useTranslation(["common", "closing"]);

  const { closingId, fundFamilyId } = useOutletContext<ClosingTabProps>();
  const queryClient = useQueryClient();
  const { data: closingData } = useClosingRetrieveSuspense(
    fundFamilyId,
    closingId,
  );

  const { data: fundFamilyConfigData } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  // prevent user from editing the interest calculation date if the equalization interest reference date is set to closing date
  const canEditInterestDate =
    fundFamilyConfigData.config.equalization_interest_reference_date !==
    EqualizationConfigDateEnum.closing_date;

  const {
    mutateAsync: createEqualization,
    isPending: isCreateEqualizationLoading,
  } = useEqualizationCreate({
    mutation: {
      onMutate: () =>
        showToast({
          message: t("closing:equalizationPreviewCreate"),
          variant: "info",
        }),
      onError: showErrorToast,
    },
  });

  const { data: subEvents, refetch: refetchSubEvents } =
    useFundFamilySubEventListSuspense(
      fundFamilyId,
      closingData.fund_family_event_id,
    );

  const equalizationSubEvent = find(
    subEvents,
    (subEvent) => subEvent.type === FundFamilySubEventTypeEnum.equalization,
  );

  const equalizationDate =
    equalizationSubEvent?.metadata?.interest_calculation_date_override ||
    closingData.closing_date;

  const refreshEqualization = async (date: string) => {
    if (!closingData.fund_family_event_id) {
      return;
    }

    await createEqualization({
      fundFamilyId,
      fundFamilyEventId: closingData.fund_family_event_id,
      data: {
        interest_calculation_date_override: canEditInterestDate
          ? date
          : undefined,
      },
    });
    await refetchSubEvents();
    await queryClient.invalidateQueries({
      queryKey: getFundFamilyEventRetrieveQueryKey(
        fundFamilyId,
        closingData.fund_family_event_id,
      ),
    });
  };
  const {
    isOpen: isEditDateModalOpen,
    onOpen: openEditDateModal,
    onClose: closeEditDateModal,
  } = useDisclosure();

  const runEqualizationButton = (
    <Button
      colorScheme="primary"
      size="xs"
      onClick={() => refreshEqualization(equalizationDate)}
      isLoading={isCreateEqualizationLoading}
    >
      {t("common:refresh")}
    </Button>
  );

  const editDateModal = async (date: string) => {
    await refreshEqualization(date);
  };
  return (
    <PageTabBody isFullHeight={false}>
      <TableBox>
        <TableBoxHeader>
          <TableBoxTitle>{t("common:equalization")}</TableBoxTitle>
          <TableBoxBadges>
            {canEditInterestDate && (
              <Badge variant="button" onClick={openEditDateModal}>
                {t("closing:editEqualizationDateModal.tooltip", {
                  effective_date: formatIsoDateToDisplay(equalizationDate),
                })}

                <Edit2Icon />
              </Badge>
            )}
          </TableBoxBadges>
          <DataTableBoxActions>{runEqualizationButton}</DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          {equalizationSubEvent ? (
            <EqualizationPreviewTable
              fundFamilyId={fundFamilyId}
              fundFamilyEventId={closingData.fund_family_event_id}
              equalizationSubEvent={equalizationSubEvent}
              isCreateEqualizationLoading={isCreateEqualizationLoading}
            />
          ) : (
            <EmptyState title={t("closing:equalizationPreviewEmptyTitle")}>
              {runEqualizationButton}
            </EmptyState>
          )}
        </TableBoxBody>
      </TableBox>
      {isEditDateModalOpen && (
        <EqualizationEditDateModal
          equalizationDate={equalizationDate}
          onSubmit={editDateModal}
          onCloseComplete={closeEditDateModal}
        />
      )}
    </PageTabBody>
  );
};

export const EqualizationPreviewTable: React.FC<{
  fundFamilyId: string;
  fundFamilyEventId: string;
  equalizationSubEvent: FundFamilySubEventData;
  isCreateEqualizationLoading: boolean;
}> = ({
  fundFamilyId,
  fundFamilyEventId,
  equalizationSubEvent,
  isCreateEqualizationLoading,
}) => {
  const { showToast } = useToast();
  const { t } = useTranslation(["common", "closing"]);

  const { data, refetch } = useEqualizationRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
    equalizationSubEvent.id!,
  );

  const renderToasts = (isError: boolean) => {
    if (isError) {
      showToast({
        message: t("closing:equalizationPreviewError"),
        variant: "error",
      });
    } else {
      showToast({
        message: t("closing:equalizationPreviewSuccess"),
        variant: "success",
      });
    }
  };

  const workflowStatuses = useCeleryWorkflowPoll(
    [equalizationSubEvent.celery_workflow_id!],
    renderToasts,
    refetch,
    () => refetch(),
  );

  const isLoading =
    isCreateEqualizationLoading ||
    workflowStatuses.get(equalizationSubEvent.celery_workflow_id!)?.state ===
      CeleryWorkflowStateMachineFieldsEnum.processing;

  return (
    <DataTable
      data={data}
      stylesForCell={stylesForCell}
      isLoading={isLoading}
    />
  );
};

const stylesForCell: StylesForCellFn = ({
  type,
  columnContext: { column, rootColumn },
}) => {
  if (type === "header" && column.columns?.length) {
    if (column === rootColumn) {
      return {
        bg: "white",
        px: "8px !important",
        justifyContent: "flex-start !important",
        "& .mb-header-cell-comp-title": {
          color: "black",
        },
      };
    } else {
      return {
        bg: "gray.25",
      };
    }
  }
  return rootColumn?.id === "current_equalization_totals" ||
    column.id === "current_equalization_totals"
    ? {
        bg: "primary.50",
        color: "primary.500",
      }
    : undefined;
};
